// Redux + routing
import {connect, useDispatch, useSelector} from "react-redux";
import {Redirect, Route, Switch, useLocation} from "react-router-dom";
import {useEffect, useState, lazy, Suspense} from "react";
import {bindActionCreators} from "redux";
import {eng, tr} from "./util/languageData";
import {actionCreators, State} from "./state/index";
import authService from "./services/authService";
import {trackPageView} from "./util/analytics";
import {initCrisp} from "./util/crisp";
import {captureAttribution} from "./util/attribution";
import "./custom.scss";

// Public, pre-rendered marketing pages - eager so react-snap captures them
// synchronously at build time (they must be in the main bundle for hydration).
import Home from "./components/home";
import ScrollToTop from "./components/ScrollToTop";
import AboutPage from "./components/about";
import ContactForm from "./components/contact/ContactForm";
import BlogList from "./components/blog/BlogList";
import BlogPost from "./components/blog/BlogPost";
import FraudPrevention from "./components/FraudPrevention";
import LandingPage from "./components/LandingPage";
import PricingPage from "./components/PricingPage";
import AnswersPage from "./components/AnswersPage";
import Navigation from "./components/navigation";
import GlobalAlert from "./components/GlobalAlert";
import AccessBlockGuard from "./components/access/AccessBlockGuard";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import CallButton from "./components/CallButton";
import KvkkPage from "./components/KvkkPage";
import LegalPage from "./components/legal/LegalPage";
import {languageForPath} from "./util/publicRoutes";
import {isAppRoute} from "./util/appRoutes";
import NotFound from "./components/NotFound";
import authStorage from "./services/authStorage";

// Authenticated / admin app - lazy-loaded so the marketing pages don't ship
// Material-UI, xlsx, jspdf, html2canvas, etc. These routes are never
// pre-rendered, so code-splitting them has no effect on SEO/hydration.
// Pre-rendered public pages must NOT be lazy: hydrate() would swap their
// static HTML for the Suspense fallback until the chunk loads (content flash).
const Login = lazy(() => import("./components/login"));
const Logout = lazy(() => import("./components/logout"));
const DesktopAuthorize = lazy(() => import("./components/desktop/DesktopAuthorize"));
const UserRegistration = lazy(() => import("./components/auth/UserRegistration"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/auth/ResetPassword"));
const NewsletterStatus = lazy(() => import("./components/newsletter/NewsletterStatus"));
const SubscriptionExpired = lazy(() => import("./components/access/SubscriptionExpired"));
const CompanyDeactivated = lazy(() => import("./components/access/CompanyDeactivated"));
const MySubscription = lazy(() => import("./components/subscription/MySubscription"));
const SubscriptionSuccess = lazy(() => import("./components/subscription/SubscriptionSuccess"));
const ShiftCashPost = lazy(() => import("./components/shiftcash/shiftCashPost"));
const ShiftReview = lazy(() =>
  import("./components/shiftreview/shiftreview").then((m) => ({default: m.ShiftReview}))
);
const EmployeeCreditCard = lazy(() =>
  import("./components/employeeCreditCard/employeeCreditCard")
);
const AdminShift = lazy(() => import("./components/admin/shift"));
const EmployeeDeficit = lazy(() => import("./components/deficit/employeeDeficit"));
const AdminDeposit = lazy(() => import("./components/admin/deposit/deposit"));
const AdminCreditCard = lazy(() => import("./components/admin/creditcard/creditCard"));
const CompanyUserManagement = lazy(() =>
  import("./components/admin/company/CompanyUserManagement")
);
const SalesAnalytics = lazy(() => import("./components/analytics/SalesAnalytics"));
const SalesReview = lazy(() => import("./components/analytics/SalesReview"));
const LiveSales = lazy(() => import("./components/analytics/LiveSales"));
const Tanks = lazy(() => import("./components/tanks/Tanks"));
const ReconciliationAmbiguityQueue = lazy(() =>
  import("./components/admin/reconciliation/ambiguityQueue")
);
const ReconciliationWorkspaceV2 = lazy(() =>
  import("./components/admin/reconciliationV2/workspace")
);
const PaymentTypeCorrectionPage = lazy(() =>
  import("./components/admin/paymentTypeCorrection/page")
);
const FuelGrossProfit = lazy(() => import("./components/finance/FuelGrossProfit"));
const SaleEarnings = lazy(() => import("./components/finance/SaleEarnings"));
const SettlementCheck = lazy(() => import("./components/finance/SettlementCheck"));

function App(props: any) {
  // const [appLanguage, setAppLanguage] = useState("en");
  // const { dispatch } = props ?? {};

  // useEffect(() => {
  //   let languageData;
  //   if (
  //     appLanguage === "en" ||
  //     appLanguage === "en-GB" ||
  //     appLanguage === "en-US"
  //   ) {
  //     languageData = eng;
  //   }
  //   if (appLanguage === "tr" || appLanguage === "tr-TR") {
  //     languageData = tr;
  //   }
  //   dispatch(actionSetAppLanguage(appLanguage, languageData));
  // }, [appLanguage, props.language?.appLanguage]);

  // Redux
  const [roles, setRoles] = useState<any>();
  const dispatch = useDispatch();
  const location = useLocation();
  const {updateUserRoles} = bindActionCreators(
    actionCreators.authActions,
    dispatch
  );

  const {setAppLanguage} = bindActionCreators(
    actionCreators.languageActions,
    dispatch
  );

  const state = useSelector((state: State) => state);
  const {isLoggedIn, user} = state.authReducer;

  useEffect(() => {
    if (user && user.roles) {
      setRoles(user.roles);
    }
  }, [user]);

  useEffect(() => {
    console.log("Running environment: " + process.env.NODE_ENV);
    // Do NOT proactively refresh here. getAndUpdateUserRoles() below makes an
    // authenticated call on mount; if the access token has expired the global
    // interceptor performs ONE shared cookie-refresh and replays it. Calling
    // refresh() here as well caused a double refresh, and because the refresh
    // token is rotated + denylisted server-side, the second call used a consumed
    // token, 401'd, and the old `.catch(logout)` then revoked + cleared the
    // cookie (that's why a reload discarded the refresh-token cookie). The
    // interceptor's failure path is a client-only logout, so a transient refresh
    // failure never wipes the HttpOnly cookie.
    const pref = localStorage.getItem("language");
    if (pref) {
      if (pref === "en" || pref === "en-GB" || pref === "en-US") {
        dispatch(setAppLanguage("en", eng));
      } else if (pref === "tr" || pref === "tr-TR") {
        dispatch(setAppLanguage("tr", tr));
      } else {
        dispatch(setAppLanguage("en", eng));
      }
    } else if (navigator.userAgent === "ReactSnap") {
      // During react-snap pre-rendering there is no stored preference and the
      // headless browser reports English. Force Turkish so the statically
      // generated HTML carries the Turkish content crawlers should index.
      dispatch(setAppLanguage("tr", tr));
    } else {
      const browserLanguage = getBrowserLanguage();
      switch (browserLanguage) {
        case "tr-TR":
          dispatch(setAppLanguage("tr", tr));
          break;
        case "tr":
          dispatch(setAppLanguage("tr", tr));
          break;
        case "en":
          dispatch(setAppLanguage("en", eng));
          break;
        case "en-GB":
          dispatch(setAppLanguage("en", eng));
          break;
        case "en-US":
          dispatch(setAppLanguage("en", eng));
          break;
        default:
          dispatch(setAppLanguage("en", eng));
      }
    }
    // Only logged-in visitors have roles. Without an access token the call can
    // only 401: it did so for every anonymous visitor and for Googlebot's
    // renderer (about 10% of Google's crawl requests were these 401s).
    if (authStorage.getAccessToken()) {
      getAndUpdateUserRoles();
    }
  }, []);

  // On every bilingual public page the URL decides the language: English
  // under /en, Turkish at the root. The rendered page must match its URL,
  // <html lang> and hreflang, also for visitors and crawlers with an English
  // browser and no stored preference (Googlebot's renderer reports en-US and
  // used to get English copy on the Turkish URLs, duplicating the /en pages).
  // Other routes (login, the app) keep the stored or browser language.
  useEffect(() => {
    const lang = languageForPath(location.pathname);
    if (lang === "en") {
      dispatch(setAppLanguage("en", eng));
    } else if (lang === "tr") {
      dispatch(setAppLanguage("tr", tr));
    }
  }, [location.pathname]);

  // Report a page view to the analytics providers on every route change. The
  // helper lazily initialises gtag/Pixel (so the first navigation counts) and
  // no-ops during react-snap pre-rendering.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Load the Crisp chatbox once, in the URL's language. No-ops during
  // react-snap pre-rendering and when the Website ID is empty (e.g. previews).
  // Also snapshot first-touch attribution (landing path/referrer/UTM) before
  // any SPA navigation rewrites the URL; lead forms attach it on submit.
  useEffect(() => {
    captureAttribution();
    const isEn =
      location.pathname === "/en" || location.pathname.startsWith("/en/");
    initCrisp(isEn ? "en" : "tr");
    // Intentionally run once on mount; Crisp self-manages after load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAndUpdateUserRoles = async () => {
    try {
      const response = await authService.getUserRoles();
      dispatch(updateUserRoles(response.data));
    } catch (e: any) {
      console.dir(e);
    }
  };

  const getBrowserLanguage = () => {
    console.log(window.navigator);
    console.log(window.navigator.language);
    console.log(window.navigator.languages);
    if (window.navigator.languages) {
      return window.navigator.languages[0];
    } else {
      return window.navigator.language;
    }
  };

  return (
    <div>
      <ScrollToTop/>
      <GlobalAlert/>
      {/* Enforces whole-tenant access blocks (subscription-expired / company-offboarded) the HTTP
          interceptor raises, routes to the right screen from anywhere. Renders nothing when normal. */}
      <AccessBlockGuard/>
      <div className="navigator">
        <Navigation/>
        <Suspense
          fallback={
            <div style={{padding: "120px 20px", textAlign: "center", color: "#64748b"}}>
              …
            </div>
          }
        >
        <Switch>
          <Route path="/" exact component={Home}/>
          {/* English (SEO) variants of the public pages */}
          <Route path="/en" exact component={Home}/>
          <Route path="/en/about" exact component={AboutPage}/>
          <Route path="/en/contact" exact component={ContactForm}/>
          {/* Blog (bilingual) */}
          <Route path="/blog" exact component={BlogList}/>
          <Route path="/blog/:slug" component={BlogPost}/>
          <Route path="/en/blog" exact component={BlogList}/>
          <Route path="/en/blog/:slug" component={BlogPost}/>
          {/* Newsletter double opt-in landing pages (backend redirect targets,
              reached via one-time email links, not prerendered or in the sitemap) */}
          <Route path="/newsletter/confirmed" exact render={() => <NewsletterStatus variant="confirmed"/>}/>
          <Route path="/newsletter/unsubscribed" exact render={() => <NewsletterStatus variant="unsubscribed"/>}/>
          <Route path="/en/newsletter/confirmed" exact render={() => <NewsletterStatus variant="confirmed"/>}/>
          <Route path="/en/newsletter/unsubscribed" exact render={() => <NewsletterStatus variant="unsubscribed"/>}/>
          {/* KVKK privacy notice (bilingual, linked from the forms) */}
          <Route path="/kvkk" exact component={KvkkPage}/>
          <Route path="/en/kvkk" exact component={KvkkPage}/>
          {/* Legal / compliance pages (lazy, reachable via the footer, required for
              İyzico merchant activation). TR at root, EN under /en. */}
          <Route path="/mesafeli-satis-sozlesmesi" exact render={() => <LegalPage docKey="distanceSales"/>}/>
          <Route path="/en/distance-sales" exact render={() => <LegalPage docKey="distanceSales"/>}/>
          <Route path="/teslimat-iade" exact render={() => <LegalPage docKey="deliveryReturn"/>}/>
          <Route path="/en/delivery-return" exact render={() => <LegalPage docKey="deliveryReturn"/>}/>
          <Route path="/gizlilik" exact render={() => <LegalPage docKey="privacy"/>}/>
          <Route path="/en/privacy" exact render={() => <LegalPage docKey="privacy"/>}/>
          {/* Fraud prevention (bilingual) */}
          <Route path="/fraud-prevention" exact component={FraudPrevention}/>
          <Route path="/en/fraud-prevention" exact component={FraudPrevention}/>
          {/* Pricing (bilingual, distinct slugs: tr /fiyatlandirma <-> en /pricing) */}
          <Route path="/fiyatlandirma" exact component={PricingPage}/>
          <Route path="/en/pricing" exact component={PricingPage}/>
          {/* Quotable answers (FAQ hub). */}
          <Route path="/sss" exact component={AnswersPage}/>
          <Route path="/en/faq" exact component={AnswersPage}/>
          {/* SEO landing pages (bilingual, exact-match head terms) */}
          <Route path="/akaryakit-istasyonu-yazilimi" exact component={LandingPage}/>
          <Route path="/benzin-istasyonu-yazilimi" exact component={LandingPage}/>
          <Route path="/vardiya-alma-programi" exact component={LandingPage}/>
          <Route path="/akaryakit-istasyonu-muhasebe-programi" exact component={LandingPage}/>
          <Route path="/nakit-yatirma-dogrulama" exact component={LandingPage}/>
          <Route path="/kredi-karti-odeme-dogrulama" exact component={LandingPage}/>
          <Route path="/canli-satis-takibi" exact component={LandingPage}/>
          <Route path="/tank-stok-takibi" exact component={LandingPage}/>
          <Route path="/akaryakit-siparis-onerisi" exact component={LandingPage}/>
          <Route path="/en/gas-station-software" exact component={LandingPage}/>
          <Route path="/en/fuel-station-software" exact component={LandingPage}/>
          <Route path="/en/shift-management-program" exact component={LandingPage}/>
          <Route path="/en/gas-station-accounting-reconciliation" exact component={LandingPage}/>
          <Route path="/en/deposit-verification" exact component={LandingPage}/>
          <Route path="/en/credit-card-payment-verification" exact component={LandingPage}/>
          <Route path="/en/live-sales-dashboard" exact component={LandingPage}/>
          <Route path="/en/fuel-tank-monitoring" exact component={LandingPage}/>
          <Route path="/en/fuel-order-suggestions" exact component={LandingPage}/>
          <Route path="/login" component={Login}/>
          <Route path="/logout" component={Logout}/>
          {/* Tenant access-block screens (subscription lapsed / company deactivated). Reached via the
              AccessBlockGuard above; not role-gated and not crawlable. */}
          <Route path="/subscription-expired" component={SubscriptionExpired}/>
          <Route path="/company-deactivated" component={CompanyDeactivated}/>
          {/* İyzico return target after the hosted checkout (public callback bounces the browser here with
              ?token=). */}
          <Route path="/subscription/success" exact component={SubscriptionSuccess}/>
          {/* Public password-reset flow. The reset-link route must precede the parent so the
              prefix "/forgot-password" doesn't swallow it. */}
          <Route path="/forgot-password/reset-link" component={ResetPassword}/>
          <Route path="/forgot-password" exact component={ForgotPassword}/>
          {/* Desktop importer "Sign in with browser" consent page. */}
          <Route path="/desktop/authorize" component={DesktopAuthorize}/>

          {roles?.length > 0 && (
            <Route
              path="/admin/shift"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <AdminShift {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              path="/admin/creditcard"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <AdminCreditCard {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}
          {roles?.length > 0 && (
            <Route
              path="/admin/deposit"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <AdminDeposit {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              path="/admin/employeedeficit"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <EmployeeDeficit {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/reconciliation/ambiguities"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <ReconciliationAmbiguityQueue {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/reconciliation/v2/workspace"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <ReconciliationWorkspaceV2/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/payment-type-correction"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <PaymentTypeCorrectionPage/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/finance/gross-profit"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <FuelGrossProfit/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/finance/sales"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <SaleEarnings/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/finance/settlement"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <SettlementCheck/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/account/subscription"
              render={() =>
                roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_USER") ? (
                  <MySubscription/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              path="/analytics/sales/review"
              render={() =>
                roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_USER") ? (
                  <SalesReview/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/analytics/live"
              render={() =>
                roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_USER") ? (
                  <LiveSales/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/tanks"
              render={() =>
                roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_USER") ? (
                  <Tanks/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/analytics/sales"
              render={() =>
                roles?.includes("ROLE_ADMIN") || roles?.includes("ROLE_USER") ? (
                  <SalesAnalytics/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              path="/shiftreview"
              render={() =>
                roles?.includes("ROLE_ADMIN") ||
                roles?.includes("ROLE_USER") ||
                roles?.includes("ROLE_CREDITCARD") ? (
                  <ShiftReview {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              path="/employeecreditcard"
              render={() =>
                roles?.includes("ROLE_ADMIN") ||
                roles?.includes("ROLE_USER") ||
                roles?.includes("ROLE_CREDITCARD") ? (
                  <EmployeeCreditCard {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}
          {/* {roles?.includes("ROLE_ADMIN") ||
          roles?.includes("ROLE_USER") ||
          roles?.includes("ROLE_CASH") ? (
            <Route path="/cash" component={ShiftCashPost} />
          ) : null} */}
          {roles?.length > 0 && (
            <Route
              path="/cash"
              render={() =>
                roles?.includes("ROLE_ADMIN") ||
                roles?.includes("ROLE_USER") ||
                roles?.includes("ROLE_CASH") ? (
                  <ShiftCashPost {...props} />
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}

          {roles?.length > 0 && (
            <Route
              exact
              path="/admin/company/users"
              render={() =>
                roles?.includes("ROLE_ADMIN") ? (
                  <CompanyUserManagement/>
                ) : (
                  <Redirect to="/"/>
                )
              }
            />
          )}
          <Route path="/contact" exact component={ContactForm}/>
          <Route path="/users/register" component={UserRegistration}/>
          <Route path="/about" exact component={AboutPage}/>
          <Route path="/employeedeficit" component={EmployeeDeficit}/>
          {/* Unknown URLs (must stay last): the host answers them with HTTP 404
              (public/_redirects), so show a helpful not-found page. App routes
              are excluded: their role-gated <Route>s mount only after the roles
              load and must never flash "not found" meanwhile. */}
          <Route render={({location}) => (isAppRoute(location.pathname) ? null : <NotFound/>)}/>
        </Switch>
        </Suspense>
      </div>
      {/* Site-wide footer, deliberately OUTSIDE .navigator so the app-shell `.navigator a` link
          styling doesn't leak onto the footer links. Carries the legal nav + payment trust badges. */}
      <Footer/>
      <WhatsAppButton/>
      <CallButton/>
    </div>
  );
}

function mapStateToProps(state: any) {
  const {user} = state.auth ?? {};
  const {...language} = state.languageReducer ?? {};
  return {
    user,
    language,
  };
}

export default connect(mapStateToProps)(App);
